import { motion, useReducedMotion } from "framer-motion";

/**
 * WJEnvironment — the WJ Core's *field*.
 *
 * The WJ monogram is the source. This is everything that radiates out from
 * it and belongs to the whole first viewport:
 *   • enormous concentric rings anchored on the WJ mark position (which is
 *     itself at ~60% x, mid-height) so they extend well into the left copy
 *     area — the headline exists *inside* this field, not beside it
 *   • curved dimensional arcs (not straight lines) for depth
 *   • broad radial glows placed unevenly for organic light distribution
 *   • scattered editorial stage words (IDEA / DESIGN / BUILD / DELIVER /
 *     LAUNCH) art-directed around the WJ so they read intentionally
 *
 * Cycles in perfect sync with WJCore (16-second lifecycle).
 */

const CYCLE = 16;
const t = (s) => s / CYCLE;

// Deterministic pseudo-random (mulberry-style) so the starfield is stable across renders.
function seedRand(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let x = s;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

// Build a starfield ring band inside the vortex zone (radii 250-560 from
// the portal centre 600,600). Split into an emerald-tinted set for the
// left/upper hemisphere and a blue-tinted set for the right/lower — the
// two nested groups counter-rotate to create the vortex feeling.
function buildStarfield(seed, count, hemisphere) {
  const rand = seedRand(seed);
  const stars = [];
  for (let i = 0; i < count; i++) {
    // radius band with density weighted toward the inner edge (near the portal)
    const rr = 260 + Math.pow(rand(), 1.6) * 300; // 260..560, biased inner
    // angle limited to the hemisphere but with generous overlap
    let angle;
    if (hemisphere === "left") {
      angle = Math.PI * 0.55 + rand() * Math.PI * 0.9; // ~100°..262°
    } else {
      angle = -Math.PI * 0.45 + rand() * Math.PI * 0.9; // ~-81°..+81°
    }
    const cx = 600 + Math.cos(angle) * rr;
    const cy = 600 + Math.sin(angle) * rr;
    const size = 0.35 + rand() * 1.5;
    const opacity = 0.2 + rand() * 0.7;
    stars.push({ cx, cy, r: size, opacity });
  }
  return stars;
}

const NEBULA_STARS_LEFT  = buildStarfield(42, 60, "left");
const NEBULA_STARS_RIGHT = buildStarfield(87, 60, "right");
// Sparse outer starfield ring — a third orbital layer rotating at yet
// another speed for perceptible parallax between the three layers.
const NEBULA_STARS_OUTER = (() => {
  const rand = seedRand(211);
  const stars = [];
  for (let i = 0; i < 42; i++) {
    const angle = rand() * Math.PI * 2;
    const rr = 470 + rand() * 180; // 470..650, well outside the mid vortex
    stars.push({
      cx: 600 + Math.cos(angle) * rr,
      cy: 600 + Math.sin(angle) * rr,
      r: 0.5 + rand() * 1.2,
      opacity: 0.2 + rand() * 0.55,
    });
  }
  return stars;
})();

// Stage words with intentional art-directed positions relative to the
// hero canvas (percentages of the whole hero, not the WJ container).
// LAUNCH sits at the same x anchor as the WJ mark (~60%) and is the
// most dominant moment.
const STAGES = [
  { word: "IDEA",    start: 2.0, peak: 2.4, fadeStart: 3.0, end: 3.4, top: "20%", left: "44%",  size: "clamp(2rem, 5.5vw, 4.8rem)" },
  { word: "DESIGN",  start: 3.4, peak: 3.8, fadeStart: 4.4, end: 4.8, top: "62%", left: "36%",  size: "clamp(2.2rem, 5.5vw, 5rem)" },
  { word: "BUILD",   start: 4.8, peak: 5.2, fadeStart: 5.8, end: 6.2, top: "18%", right: "8%",  size: "clamp(2rem, 5vw, 4.6rem)" },
  { word: "DELIVER", start: 6.2, peak: 6.6, fadeStart: 7.2, end: 7.6, top: "70%", right: "6%",  size: "clamp(2rem, 5vw, 4.6rem)" },
  { word: "LAUNCH",  start: 7.6, peak: 8.4, fadeStart: 9.4, end: 10.0, top: "42%", left: "60%", size: "clamp(3.4rem, 10vw, 9rem)", strong: true, tx: "-50%" },
];

export default function WJEnvironment() {
  const reduce = useReducedMotion();

  const loopTrans = (times) => ({
    times,
    duration: reduce ? 0 : CYCLE,
    repeat: reduce ? 0 : Infinity,
    ease: "easeInOut",
  });

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      {/* ─── Uneven organic radial glows — broad falloff, layered depth.
             Static opacities (cycling them cost 4 extra Framer tracks for
             negligible visual value). Mouse parallax applied via CSS vars. ─── */}
      <div className="absolute inset-0 wj-drift-40">
        <div>
          <div className="absolute left-[42%] top-[26%] w-[70%] h-[70%] rounded-full bg-[#0E9D6E] blur-[180px] opacity-[0.12]" />
          <div className="absolute left-[-6%] top-[38%] w-[55%] h-[55%] rounded-full bg-[#0B4B6F] blur-[160px] opacity-[0.1]" />
          <div className="absolute right-[-4%] bottom-[8%] w-[50%] h-[50%] rounded-full bg-[#2B8AF5] blur-[170px] opacity-[0.11]" />
          <div className="absolute left-[8%] bottom-[12%] w-[30%] h-[30%] rounded-full bg-[#0E9D6E] blur-[140px] opacity-[0.07]" />
        </div>
      </div>

      {/* ─── Enormous concentric orbital field — anchored on the WJ mark
             position (~62% x, 50% y). Sized to extend well past the left
             edge so headline sits *inside* it. ─── */}
      <svg
        className="absolute top-1/2 left-[62%] -translate-x-1/2 -translate-y-1/2 wj-env-orbit wj-drift-40"
        viewBox="0 0 1200 1200"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="wj-env-arc-em" x1="0%" y1="80%" x2="100%" y2="20%">
            <stop offset="0%" stopColor="#3fdca6" stopOpacity="0" />
            <stop offset="50%" stopColor="#3fdca6" stopOpacity="1" />
            <stop offset="100%" stopColor="#3fdca6" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="wj-env-arc-blue" x1="100%" y1="20%" x2="0%" y2="80%">
            <stop offset="0%" stopColor="#2B8AF5" stopOpacity="0" />
            <stop offset="50%" stopColor="#2B8AF5" stopOpacity="1" />
            <stop offset="100%" stopColor="#2B8AF5" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="wj-nebula-em" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0E9D6E" stopOpacity="0" />
            <stop offset="40%" stopColor="#3fdca6" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0E9D6E" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="wj-nebula-blue" x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0B4B6F" stopOpacity="0" />
            <stop offset="45%" stopColor="#2B8AF5" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0B4B6F" stopOpacity="0" />
          </linearGradient>
          <filter id="wj-env-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="4" />
          </filter>
          <filter id="wj-nebula-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="9" />
          </filter>
        </defs>

        {/* ─── Nebula vortex — dense swirling starfield + smoky curves.
              All rotation is native CSS (compositor thread, GPU-friendly).
              Blur is applied ONLY to non-rotating layers to avoid the
              browser re-rasterizing blurred SVG every frame. ─── */}

        {/* Emerald hemisphere — spins clockwise via CSS */}
        <g className="wj-orbit-em">
          {/* Nebula curves — no blur filter; thick semi-transparent stroke
              gives the smoky feel without frame-by-frame rasterization */}
          <path
            d="M 260 720 Q 380 300 780 220 T 1080 500"
            fill="none" stroke="#3fdca6" strokeWidth="22"
            strokeLinecap="round" opacity="0.14"
          />
          <path
            d="M 340 860 Q 620 940 940 780 T 1120 380"
            fill="none" stroke="#3fdca6" strokeWidth="14"
            strokeLinecap="round" opacity="0.1"
          />
          {/* Emerald hemisphere starfield */}
          {NEBULA_STARS_LEFT.map((s, i) => (
            <circle
              key={`el-${i}`}
              cx={s.cx} cy={s.cy} r={s.r}
              fill={i % 5 === 0 ? "#3fdca6" : "#EFE4CE"}
              opacity={s.opacity}
            />
          ))}
        </g>

        {/* Blue hemisphere — spins counter-clockwise via CSS */}
        <g className="wj-orbit-blue">
          <path
            d="M 300 460 Q 500 240 820 360 T 1080 780"
            fill="none" stroke="#2B8AF5" strokeWidth="20"
            strokeLinecap="round" opacity="0.14"
          />
          <path
            d="M 220 560 Q 460 900 820 900 T 1120 620"
            fill="none" stroke="#2B8AF5" strokeWidth="12"
            strokeLinecap="round" opacity="0.1"
          />
          {NEBULA_STARS_RIGHT.map((s, i) => (
            <circle
              key={`er-${i}`}
              cx={s.cx} cy={s.cy} r={s.r}
              fill={i % 4 === 0 ? "#8ecbff" : "#EFE4CE"}
              opacity={s.opacity}
            />
          ))}
        </g>

        {/* Outer sparse starfield — third parallax layer via CSS */}
        <g className="wj-orbit-outer">
          {NEBULA_STARS_OUTER.map((s, i) => (
            <circle
              key={`eo-${i}`}
              cx={s.cx} cy={s.cy} r={s.r}
              fill={i % 6 === 0 ? "#F4E3B0" : "#EFE4CE"}
              opacity={s.opacity}
            />
          ))}
        </g>

        {/* Outermost giant ring — extends past the headline into the left */}
        <circle
          cx="600" cy="600" r="580"
          fill="none" stroke="#B79961" strokeWidth="0.5"
          opacity="0.13"
        />
        {/* Second giant ring */}
        <circle
          cx="600" cy="600" r="490"
          fill="none" stroke="#B79961" strokeWidth="0.45"
          opacity="0.11"
        />
        {/* Third ring — a little tighter but still large */}
        <circle
          cx="600" cy="600" r="410"
          fill="none" stroke="#B79961" strokeWidth="0.4"
          opacity="0.1"
        />

        {/* Portal — a bright defined ring immediately containing the WJ */}
        <circle
          cx="600" cy="600" r="240"
          fill="none" stroke="#B79961" strokeWidth="1.1"
          opacity="0.55"
        />
        {/* Portal — dashed inner boundary (like the reference) */}
        <circle
          cx="600" cy="600" r="215"
          fill="none" stroke="#B79961" strokeWidth="0.7"
          strokeDasharray="4 6"
          opacity="0.4"
        />

        {/* Horizontal centre beam — bright bilateral gold light emerging
            from the WJ during LAUNCH build-up */}
        <motion.line
          x1="600" y1="600" x2="60" y2="600"
          stroke="#F4E3B0" strokeWidth="0.9"
          filter="url(#wj-env-glow)"
          initial={{ opacity: 0 }}
          animate={{
            opacity: reduce ? 0.6 : [0.1, 0.15, 0.35, 0.9, 0.4, 0.1],
          }}
          transition={loopTrans([0, t(2), t(5), t(8.4), t(11), 1])}
        />
        <motion.line
          x1="600" y1="600" x2="1140" y2="600"
          stroke="#F4E3B0" strokeWidth="0.9"
          filter="url(#wj-env-glow)"
          initial={{ opacity: 0 }}
          animate={{
            opacity: reduce ? 0.6 : [0.1, 0.15, 0.35, 0.9, 0.4, 0.1],
          }}
          transition={loopTrans([0, t(2), t(5), t(8.4), t(11), 1])}
        />

        {/* Curved dimensional arcs — static baseline (no per-frame opacity animation) */}
        <path
          d="M 30 700 A 610 610 0 0 1 260 130"
          fill="none" stroke="url(#wj-env-arc-em)" strokeWidth="1.4"
          opacity="0.28"
        />
        <path
          d="M 1160 500 A 610 610 0 0 1 940 1070"
          fill="none" stroke="url(#wj-env-arc-blue)" strokeWidth="1.4"
          opacity="0.24"
        />
        <path
          d="M 70 820 A 720 720 0 0 1 200 260"
          fill="none" stroke="#B79961" strokeWidth="0.35"
          opacity="0.1"
        />

        {/* Accent dots on the outermost ring */}
        <circle cx="20" cy="600" r="4" fill="#28B37A" opacity="0.55" />
        <circle cx="600" cy="20" r="3.5" fill="#7B4BCC" opacity="0.45" />
        <circle cx="1180" cy="600" r="3" fill="#2B8AF5" opacity="0.4" />
      </svg>

      {/* ─── Art-directed editorial stage words positioned across the whole hero ─── */}
      {!reduce && (
        <div className="absolute inset-0 hidden md:block">
          {STAGES.map((s) => (
            <motion.span
              key={s.word}
              className={
                "wj-stage-word absolute font-display uppercase tracking-[0.28em] font-light " +
                (s.strong ? "wj-stage-word--launch" : "")
              }
              style={{
                top: s.top,
                left: s.left,
                right: s.right,
                fontSize: s.size,
                transform: s.tx ? `translateX(${s.tx})` : undefined,
              }}
              animate={{
                opacity: [0, 0, s.strong ? 0.55 : 0.28, s.strong ? 0.5 : 0.24, 0, 0],
                scale: [0.95, 0.95, 1, 1, 0.98, 0.95],
              }}
              transition={loopTrans([0, t(s.start), t(s.peak), t(s.fadeStart), t(s.end), 1])}
            >
              {s.word}
            </motion.span>
          ))}
        </div>
      )}
    </div>
  );
}
